// src/services/raffleService.js
const API_BASE_URL = process.env.NODE_ENV === 'development' ? '/api' : 'https://nlngraclub.com/nlnapi';
const getToken = () => localStorage.getItem('nlngra_token');

const handleResponse = async(response) => {
    const text = await response.text();
    const jsonMatch = text.match(/\{.*\}/s);
    if (jsonMatch) {
        try {
            return JSON.parse(jsonMatch[0]);
        } catch (e) {
            console.error('JSON parse error:', e);
            throw new Error('Invalid JSON format');
        }
    }
    throw new Error('Invalid response format');
};

export const raffleService = {
    // ============================================
    // USER ENDPOINTS
    // ============================================

    // Get active raffle events
    getActiveEvents: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=get_active_events&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get active events error:', error);
            throw error;
        }
    },

    // Get single event details
    getEvent: async(eventId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=get_event&event_id=${eventId}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get event error:', error);
            throw error;
        }
    },

    // Purchase tickets
    purchaseTickets: async(eventId, quantity) => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=purchase_tickets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ event_id: eventId, quantity })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Purchase tickets error:', error);
            throw error;
        }
    },

    // Upload payment proof
    uploadPaymentProof: async(paymentId, referenceNumber, proofImage, notes = '') => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=upload_payment_proof`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    payment_id: paymentId,
                    reference_number: referenceNumber,
                    proof_image: proofImage,
                    notes: notes
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Upload payment proof error:', error);
            throw error;
        }
    },

    // Get user's tickets
    getMyTickets: async(eventId = null) => {
        try {
            let url = `${API_BASE_URL}/raffle.php?action=my_tickets&_t=${Date.now()}`;
            if (eventId) {
                url += `&event_id=${eventId}`;
            }
            const response = await fetch(url, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get my tickets error:', error);
            throw error;
        }
    },

    // Get user's winnings
    getMyWinnings: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=my_winnings&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get my winnings error:', error);
            throw error;
        }
    },

    // Claim prize
    claimPrize: async(resultId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=claim_prize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ result_id: resultId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Claim prize error:', error);
            throw error;
        }
    },

    // Get raffle settings
    getSettings: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=get_settings&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Get settings error:', error);
            throw error;
        }
    },

    // ============================================
    // ADMIN ENDPOINTS
    // ============================================

    // Create raffle event (admin)
    createRaffleEvent: async(eventData) => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=admin_create_event`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify(eventData)
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Create raffle event error:', error);
            throw error;
        }
    },

    // Get all events for admin
    adminGetEvents: async() => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=admin_get_events&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Admin get events error:', error);
            throw error;
        }
    },

    // Get pending payments for admin
    adminGetPayments: async(status = 'pending') => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=admin_get_payments&status=${status}&_t=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Content-Type': 'application/json'
                }
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Admin get payments error:', error);
            throw error;
        }
    },

    // Verify payment (admin)
    adminVerifyPayment: async(paymentId, action, rejectionReason = '') => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=admin_verify_payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({
                    payment_id: paymentId,
                    action: action,
                    rejection_reason: rejectionReason
                })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Admin verify payment error:', error);
            throw error;
        }
    },

    // Run raffle draw (admin)
    adminRunDraw: async(eventId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=admin_run_draw`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ event_id: eventId })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Admin run draw error:', error);
            throw error;
        }
    },

    // Update raffle settings (admin)
    adminUpdateSettings: async(settings) => {
        try {
            const response = await fetch(`${API_BASE_URL}/raffle.php?action=admin_update_settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken()
                },
                body: JSON.stringify({ settings })
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('Admin update settings error:', error);
            throw error;
        }
    }
};